import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Form } from 'react-final-form';
import { FORM_ERROR } from 'final-form';
import { FormattedMessage, injectIntl } from 'react-intl';
import { connect, useDispatch } from 'react-redux';
import { AuthProtected, BusinessCategoriesField, BusinessInfoFieldGroup, BusinessLogosFieldGroup, LocationFieldGroup, MembershipInfo } from 'containers';
import { Spinner } from 'components';
import BusinessInfoEditModal from 'components/BusinessInfoEditModal/BusinessInfoEditModal';
import { actions } from 'coupon-common/src/modules/businessInfo';
import { actions as membershipInfoActions } from 'coupon-common/src/modules/memberships/info';
import { actions as pointsBalancesActions } from 'coupon-common/src/modules/admin/pointsManager/pointsBalances';
import { dispatchAsAsync } from 'coupon-common/src/utils';
import messages from './messages';
import BusinessCard from '../Dashboard/Components/BusinessCard';
import PremiumCard from '../Dashboard/Components/PremiumCard';
import NavBar from '../Dashboard/Components/NavBar';
import cautionImg from '../../assets/images/caution.png';
import MyCouponLogo from '../../assets/images/MyCouponLogo.svg';
import './Account.responsive.css';
import { actions as emailActions } from 'coupon-common/src/modules/email';

const RED = '#C0392B';
const ICON_COLOR = '#1C1C1C';

// ── Feature flags ──────────────────────────────────────────────
// Hidden per product decision — code kept in place for a quick re-enable.
const SHOW_WEB_SOCIAL = false;
const SHOW_BILLING = false;

/* ── SVG icons ─────────────────────────────────────────────────── */
const IconShield    = () => <svg width="22" height="22" viewBox="0 0 22 22" fill="none"><path d="M11 2L3 5.5V11c0 4.4 3.4 8.5 8 9.5 4.6-1 8-5.1 8-9.5V5.5L11 2Z" stroke={ICON_COLOR} strokeWidth="1.5" strokeLinejoin="round"/></svg>;
const IconDollar    = () => <svg width="22" height="22" viewBox="0 0 22 22" fill="none"><circle cx="11" cy="11" r="8.5" stroke={ICON_COLOR} strokeWidth="1.5"/><path d="M11 6v10M8.5 8.5c0-1.1.9-2 2.5-2s2.5.9 2.5 2-1 1.8-2.5 2-2.5.9-2.5 2 .9 2 2.5 2 2.5-.9 2.5-2" stroke={ICON_COLOR} strokeWidth="1.4" strokeLinecap="round"/></svg>;
const IconSettings2 = () => <svg width="22" height="22" viewBox="0 0 22 22" fill="none"><circle cx="11" cy="11" r="3" stroke={ICON_COLOR} strokeWidth="1.5"/><path d="M11 2v2M11 18v2M2 11h2M18 11h2M4.2 4.2l1.4 1.4M16.4 16.4l1.4 1.4M4.2 17.8l1.4-1.4M16.4 5.6l1.4-1.4" stroke={ICON_COLOR} strokeWidth="1.5" strokeLinecap="round"/></svg>;
const IconEdit      = () => <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M11 2l3 3-8 8H3v-3l8-8Z" stroke="#888" strokeWidth="1.3" strokeLinejoin="round"/></svg>;
const IconChevron   = () => <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M6 4l4 4-4 4" stroke="#aaa" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>;

/* ── Safe string helper ─────────────────────────────────────────── */
const safeStr = val => {
  if (val === null || val === undefined) return '—';
  if (typeof val === 'string') return val || '—';
  if (typeof val === 'number') return String(val);
  if (typeof val === 'object') return val.name || val.label || val.title || JSON.stringify(val);
  return String(val);
};

/* ── Membership renewal date formatter ────────────────────────────
   Matches the "Mar – 20 – 2026" em-dash style used by PremiumCard's
   own default value. Returns undefined (falls through to the card's
   default) when the date is missing or unparsable. ── */
const formatRenewalDate = iso => {
  if (!iso) return undefined;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return undefined;
  return d
    .toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' })
    .replace(/ /g, ' – ');
};

/* ── Page2-style field components ───────────────────────────────── */
const FieldLabel = ({ children }) => (
  <label style={{
    display: 'block', fontSize: 13, fontWeight: 600,
    color: '#374151', marginBottom: 6,
  }}>
    {children}
  </label>
);

const FieldInput = ({ value, onChange, placeholder, type = 'text', icon }) => (
  <div style={{ position: 'relative' }}>
    {icon && (
      <span style={{
        position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)',
        color: '#9ca3af', display: 'flex', alignItems: 'center',
      }}>{icon}</span>
    )}
    <input
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      style={{
        width: '100%', boxSizing: 'border-box',
        border: '1.5px solid #e5e7eb', borderRadius: 8,
        padding: icon ? '10px 12px 10px 34px' : '10px 12px',
        fontSize: 13, color: '#111', outline: 'none',
        background: '#fff', fontFamily: 'inherit',
        transition: 'border-color .15s',
      }}
      onFocus={e => e.target.style.borderColor = RED}
      onBlur={e => e.target.style.borderColor = '#e5e7eb'}
    />
  </div>
);

const FieldCard = ({ children }) => (
  <div style={{
    border: '1.5px solid #e5e7eb', borderRadius: 12,
    padding: '18px 16px', marginBottom: 16, background: '#fff',
  }}>
    {children}
  </div>
);

const FieldWrap = ({ children, style }) => (
  <div style={{ marginBottom: 14, ...style }}>{children}</div>
);

/* ── Shared field icon (still used by the Web & Social link form) ── */
const IcoGlobe  = () => <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><circle cx="7" cy="7" r="5.5" stroke="#9ca3af" strokeWidth="1.3"/><path d="M7 1.5C5.5 3.5 5.5 10.5 7 12.5M7 1.5C8.5 3.5 8.5 10.5 7 12.5M1.5 7h11" stroke="#9ca3af" strokeWidth="1.2"/></svg>;

const IconTrash = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path d="M3 4.5h10M6.5 4.5V3a1 1 0 0 1 1-1h1a1 1 0 0 1 1 1v1.5M4.5 4.5l.6 8.1a1 1 0 0 0 1 .9h3.8a1 1 0 0 0 1-.9l.6-8.1"
      stroke="#C0392B" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M6.7 7.2v4M9.3 7.2v4" stroke="#C0392B" strokeWidth="1.2" strokeLinecap="round"/>
  </svg>
);

/* ── Membership Modal ───────────────────────────────────────────── */
const CheckIcon = ({ dark }) => (
  <div style={{
    width: 26, height: 26, borderRadius: '50%', flexShrink: 0,
    background: dark ? 'rgba(255,255,255,0.25)' : '#fde8e8',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  }}>
    <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
      <path d="M2 6.5l3 3 6-6" stroke={dark ? '#fff' : ICON_COLOR} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  </div>
);

const PLANS = [
  {
    id: 'starter',
    tier: 'Starter',
    price: 'Free',
    priceNum: null,
    dark: false,
    popular: false,
    features: [
      'Up to 3 active coupons',
      'Basic analytics dashboard',
      'Email support',
      'Standard redemption processing',
    ],
    fee: 'Transaction fees: 4.5% + $4.95 per redemption',
    cta: 'Book a call',
    bookingUrl: 'https://calendar.app.google/8zDqrfCsSnWoLYUj9',
  },
  {
    id: 'growth',
    tier: 'Growth',
    price: '$65',
    priceNum: 65,
    period: '/month',
    dark: true,
    popular: true,
    features: [
      '20 active coupons',
      'Build for unlimited growth',
      'AI-Powered image generation',
      'Priority support & onboarding',
      'Advanced analysts & insights',
    ],
    fee: 'Transaction fees: 2.9% + $0.99 per redemption',
    cta: 'Book a call',
    bookingUrl: 'https://calendar.app.google/8zDqrfCsSnWoLYUj9',
  },
];

const MembershipModal = ({ onClose, navigate }) => {
  // Close on backdrop click
  const handleBackdrop = e => { if (e.target === e.currentTarget) onClose(); };

  // Close on Escape
  useEffect(() => {
    const handler = e => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  return (
    <div
      onClick={handleBackdrop}
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(17,24,39,0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1000, padding: 20,
        backdropFilter: 'blur(3px)',
      }}
    >
      <div style={{
        width: '100%', maxWidth: 860,
        borderRadius: 24,
        overflow: 'hidden',
        background: '#f0e8e8',
        boxShadow: '0 24px 64px rgba(0,0,0,0.22)',
        display: 'flex',
        flexDirection: 'column',
      }}>
        {/* Modal header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '20px 28px 16px',
          background: '#f0e8e8',
        }}>
          <div>
            <div style={{ fontSize: 20, fontWeight: 800, color: '#1a1a1a' }}>Choose your Plan</div>
            <div style={{ fontSize: 13, color: '#888', marginTop: 2 }}>Select a membership that fits your business</div>
          </div>
          <button
            onClick={onClose}
            style={{
              width: 34, height: 34, borderRadius: '50%',
              border: '1.5px solid #e0d0d0', background: '#fff',
              cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M1 1l12 12M13 1L1 13" stroke="#555" strokeWidth="1.6" strokeLinecap="round"/>
            </svg>
          </button>
        </div>

        {/* Plans grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: 0,
          padding: '0 20px 24px',
          gap: 16,
        }}>
          {PLANS.map(plan => (
            <div
              key={plan.id}
              style={{
                borderRadius: 20,
                background: plan.dark
                  ? 'linear-gradient(145deg, #e03030 0%, #b91c1c 100%)'
                  : '#fff',
                padding: '28px 28px 24px',
                display: 'flex',
                flexDirection: 'column',
                gap: 0,
                position: 'relative',
                boxShadow: plan.dark
                  ? '0 8px 32px rgba(192,57,43,0.35)'
                  : '0 2px 12px rgba(0,0,0,0.07)',
              }}
            >
              {/* Popular badge */}
              {plan.popular && (
                <div style={{
                  position: 'absolute', top: 24, right: 24,
                  background: '#fff', color: RED,
                  fontSize: 12, fontWeight: 700,
                  padding: '5px 14px', borderRadius: 999,
                }}>
                  Most Popular
                </div>
              )}

              {/* Tier + price */}
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontSize: 14, fontWeight: 500, color: plan.dark ? 'rgba(255,255,255,0.8)' : '#555', marginBottom: 4 }}>
                  {plan.tier}
                </div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
                  <span style={{ fontSize: 52, fontWeight: 800, color: plan.dark ? '#fff' : '#1a1a1a', lineHeight: 1 }}>
                    {plan.price}
                  </span>
                  {plan.period && (
                    <span style={{ fontSize: 15, color: plan.dark ? 'rgba(255,255,255,0.75)' : '#888', fontWeight: 400 }}>
                      {plan.period}
                    </span>
                  )}
                </div>
              </div>

              {/* Divider */}
              <div style={{ height: 1, background: plan.dark ? 'rgba(255,255,255,0.2)' : '#f0e8e8', marginBottom: 20 }} />

              {/* Features */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14, flex: 1, marginBottom: 24 }}>
                {plan.features.map((f, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <CheckIcon dark={plan.dark} />
                    <span style={{
                      fontSize: 15, fontWeight: i === 0 && !plan.dark ? 700 : 400,
                      color: plan.dark ? '#fff' : '#1a1a1a',
                    }}>
                      {f}
                    </span>
                  </div>
                ))}
              </div>

              {/* Transaction fee pill */}
              <div style={{
                background: plan.dark ? 'rgba(255,255,255,0.18)' : '#f7f0f0',
                borderRadius: 12, padding: '14px 18px',
                fontSize: 14, color: plan.dark ? '#fff' : '#555',
                marginBottom: 20, textAlign: 'center',
              }}>
                {plan.fee}
              </div>

              {/* CTA */}
              <button
                onClick={() => { onClose(); window.open(plan.bookingUrl, '_blank', 'noopener,noreferrer'); }}
                style={{
                  width: '100%', padding: '14px 0',
                  borderRadius: 999,
                  border: plan.dark ? 'none' : 'none',
                  background: plan.dark ? '#fff' : RED,
                  color: plan.dark ? RED : '#fff',
                  fontSize: 15, fontWeight: 700,
                  cursor: 'pointer', fontFamily: 'inherit',
                  transition: 'opacity .15s',
                }}
                onMouseEnter={e => e.target.style.opacity = '0.88'}
                onMouseLeave={e => e.target.style.opacity = '1'}
              >
                {plan.cta}
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

/* ── Contact Us modal ───────────────────────────────────────────────
   No "contact support" API action exists yet, so submitting just shows
   a confirmation state locally — swap handleSubmit's TODO for a real
   dispatch once that endpoint exists. ── */
const CONTACT_SUBJECTS = [
  { value: 'general', label: 'General Inquiry' },
  { value: 'billing', label: 'Billing' },
  { value: 'technical', label: 'Technical Support' },
  { value: 'coupons', label: 'Coupons & Deals' },
];

const ContactMailIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <rect x="1.5" y="3" width="13" height="10" rx="1.5" stroke={RED} strokeWidth="1.3" />
    <path d="M1.5 4l6.5 4.5L14.5 4" stroke={RED} strokeWidth="1.3" strokeLinecap="round" />
  </svg>
);

// Same button + popover treatment as SocialPlatformSelect (a native
// <select>'s open option list can't be styled), so any themed dropdown
// in this file looks the same — just without a per-option icon.
const ThemedSelect = ({ value, onChange, options }) => {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const selected = options.find(o => o.value === value) || options[0];

  useEffect(() => {
    const handleClickOutside = e => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div style={{ position: 'relative' }} ref={wrapRef}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        style={{
          width: '100%', boxSizing: 'border-box', display: 'flex', alignItems: 'center', gap: 10,
          border: `1.5px solid ${open ? RED : '#e5e7eb'}`, borderRadius: 8,
          padding: '10px 12px', fontSize: 13, color: '#111',
          background: '#fff', fontFamily: 'inherit', cursor: 'pointer',
          transition: 'border-color .15s',
        }}
      >
        <span style={{ flex: 1, textAlign: 'left' }}>{selected.label}</span>
        <span style={{ display: 'flex', transform: open ? 'rotate(90deg)' : 'none', transition: 'transform .15s' }}>
          <IconChevron />
        </span>
      </button>

      {open && (
        <div
          role="listbox"
          style={{
            position: 'absolute', top: 'calc(100% + 6px)', left: 0, right: 0,
            background: '#fff', borderRadius: 14, padding: 6,
            boxShadow: '0 12px 32px rgba(0,0,0,0.16), 0 2px 8px rgba(0,0,0,0.06)',
            zIndex: 20, display: 'flex', flexDirection: 'column', gap: 1,
          }}
        >
          {options.map(o => {
            const isSelected = o.value === value;
            return (
              <button
                key={o.value}
                type="button"
                role="option"
                aria-selected={isSelected}
                onClick={() => { onChange(o.value); setOpen(false); }}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  width: '100%', border: 'none', borderRadius: 10,
                  padding: '10px 10px', fontSize: 13, fontFamily: 'inherit',
                  textAlign: 'left', cursor: 'pointer',
                  background: isSelected ? '#fff2f2' : 'transparent',
                  color: isSelected ? RED : '#1a1a1a',
                  fontWeight: isSelected ? 700 : 500,
                }}
                onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = '#f5f5f7'; }}
                onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = 'transparent'; }}
              >
                <span>{o.label}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

const ContactUsModal = ({ onClose }) => {
  const dispatch = useDispatch();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [subject, setSubject] = useState(CONTACT_SUBJECTS[0].value);
  const [message, setMessage] = useState('');
  const [sent, setSent] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const handleBackdrop = e => { if (e.target === e.currentTarget) onClose(); };

  useEffect(() => {
    const handler = e => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

   const handleSubmit = async e => {
    e.preventDefault();
    if (!name.trim() || !email.trim() || !message.trim()) return;

    setSubmitError(null);
    const result = await dispatchAsAsync(
      emailActions.sendContactMessage({ name, email, subject, message }),
      dispatch,
    );

    if (!result) {
      setSent(true);
    } else if (result[FORM_ERROR]) {
      setSubmitError(result[FORM_ERROR]);
    }
  };

  return (
    <div
      onClick={handleBackdrop}
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(17,24,39,0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1000, padding: 20,
        backdropFilter: 'blur(3px)',
      }}
    >
      <div style={{
        position: 'relative',
        width: '100%', maxWidth: 860,
        marginTop: 60,
        borderRadius: 24,
        overflow: 'hidden',
        background: '#fff',
        boxShadow: '0 24px 64px rgba(0,0,0,0.22)',
        display: 'flex',
        flexWrap: 'wrap',
      }}>
        <button
          onClick={onClose}
          aria-label="Close"
          style={{
            position: 'absolute', top: 20, right: 20,
            width: 34, height: 34, borderRadius: '50%',
            border: '1.5px solid #e5e7eb', background: '#fff',
            cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 1,
          }}
        >
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M1 1l12 12M13 1L1 13" stroke="#555" strokeWidth="1.6" strokeLinecap="round" />
          </svg>
        </button>

        {/* Left — pitch + email */}
        <div style={{
          flex: '1 1 320px', padding: '48px 44px',
          display: 'flex', flexDirection: 'column', justifyContent: 'center',
        }}>
          <img src={MyCouponLogo} alt="MyCouponBook" style={{ alignSelf: 'flex-start', height: 64, width: 'auto', marginBottom: 20 }} />
          <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.08em', color: RED, textTransform: 'uppercase', marginBottom: 16 }}>
            We're here to help you
          </div>
          <h2 style={{ fontSize: 28, fontWeight: 800, color: '#1a1a1a', lineHeight: 1.25, margin: '0 0 14px' }}>
            Discuss Your <span style={{ color: RED }}>Business</span> Needs
          </h2>
          <p style={{ fontSize: 14, color: '#777', lineHeight: 1.6, margin: '0 0 28px' }}>
            Have a question about your account, coupons, or billing? Send us a message and our team will get back to you.
          </p>

          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{
              width: 36, height: 36, borderRadius: '50%', background: '#fbe4e1',
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}>
              <ContactMailIcon />
            </span>
            <div>
              <div style={{ fontSize: 11.5, color: '#999' }}>E-mail</div>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#1a1a1a' }}>support@mycouponbook.ca</div>
            </div>
          </div>
        </div>

        {/* Right — form card, floating on a soft gray panel */}
        <div style={{
          flex: '1 1 360px', background: '#f7f4f4',
          padding: 28, display: 'flex', alignItems: 'center',
        }}>
          <div style={{
            width: '100%', background: '#fff', borderRadius: 20,
            padding: 28, boxShadow: '0 16px 40px rgba(0,0,0,0.08)',
          }}>
            {sent ? (
              <div style={{ textAlign: 'center', padding: '24px 4px' }}>
                <div style={{ fontSize: 16, fontWeight: 800, color: '#1a1a1a', marginBottom: 8 }}>Message sent</div>
                <p style={{ fontSize: 13, color: '#777', margin: 0 }}>Thanks for reaching out — we'll get back to you soon.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit}>
                <FieldWrap>
                  <FieldLabel>Name</FieldLabel>
                  <FieldInput value={name} onChange={e => setName(e.target.value)} placeholder="Jane Smith" />
                </FieldWrap>

                <FieldWrap>
                  <FieldLabel>Email</FieldLabel>
                  <FieldInput type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="jane@name.com" />
                </FieldWrap>

                <FieldWrap>
                  <FieldLabel>Subject</FieldLabel>
                  <ThemedSelect value={subject} onChange={setSubject} options={CONTACT_SUBJECTS} />
                </FieldWrap>

                <FieldWrap style={{ marginBottom: 0 }}>
                  <FieldLabel>Message</FieldLabel>
                  <textarea
                    value={message}
                    onChange={e => setMessage(e.target.value)}
                    placeholder="Type your message"
                    rows={4}
                    style={{
                      width: '100%', boxSizing: 'border-box', resize: 'vertical',
                      border: '1.5px solid #e5e7eb', borderRadius: 8,
                      padding: '10px 12px', fontSize: 13, color: '#111',
                      background: '#fff', fontFamily: 'inherit',
                    }}
                  />
                </FieldWrap>

                {submitError && (
  <div style={{
    background: '#fff2f2', border: '1px solid #fca5a5',
    borderRadius: 8, padding: '10px 14px',
    fontSize: 13, color: RED, marginTop: 14,
  }}>
    {submitError}
  </div>
)}

                <button
                  type="submit"
                  style={{
                    marginTop: 18, width: '100%',
                    border: 'none', borderRadius: 999, padding: '15px 22px',
                    background: 'linear-gradient(145deg, #e03030 0%, #b91c1c 100%)',
                    color: '#fff', fontSize: 14, fontWeight: 700,
                    cursor: 'pointer', fontFamily: 'inherit',
                    boxShadow: '0 8px 20px rgba(192,57,43,0.35)',
                  }}
                >
                  Get a Solution
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

/* ── Web & Social links modal ──────────────────────────────────────
   Local-only for now (no backend field exists for this yet) — persisted
   to localStorage so it survives a reload instead of vanishing.
   Hidden behind SHOW_WEB_SOCIAL — component kept intact for re-enable. ── */
const WEB_SOCIAL_STORAGE_KEY = 'cb_web_social_links';
const MAX_WEB_SOCIAL_LINKS = 3;

const loadWebSocialLinks = () => {
  try {
    const raw = localStorage.getItem(WEB_SOCIAL_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const normalizeUrl = url => {
  const trimmed = url.trim();
  if (!trimmed) return trimmed;
  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
};

/* ── Social platform options — each with its own brand-colored icon ── */
const InstagramIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
    <rect x="2" y="2" width="14" height="14" rx="4" stroke="#E1306C" strokeWidth="1.4" />
    <circle cx="9" cy="9" r="3.4" stroke="#E1306C" strokeWidth="1.4" />
    <circle cx="12.8" cy="5.2" r="1" fill="#E1306C" />
  </svg>
);
const FacebookIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
    <circle cx="9" cy="9" r="7" fill="#1877F2" />
    <path d="M11.1 9.3H9.7v5.4H7.6V9.3H6.5V7.5h1.1V6.3c0-1.3.6-2.4 2.5-2.4h1.5v1.8h-1c-.4 0-.6.2-.6.6v1.2h1.6l-.4 1.8Z" fill="#fff" />
  </svg>
);
const XIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
    <rect x="1.5" y="1.5" width="15" height="15" rx="4" fill="#000" />
    <path d="M5 5l8 8M13 5l-8 8" stroke="#fff" strokeWidth="1.4" strokeLinecap="round" />
  </svg>
);
const WhatsAppIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
    <circle cx="9" cy="9" r="7" fill="#25D366" />
    <path
      d="M5.5 12.5 6 10.4a3.9 3.9 0 1 1 1.6 1.5l-2.1.6Z"
      fill="#fff"
    />
    <path
      d="M7.7 6.9c.15-.3.3-.3.45-.3h.35c.1 0 .25 0 .35.3.15.35.5 1.15.5 1.25.05.1.05.2 0 .3-.05.15-.1.2-.2.3l-.3.3c-.1.1-.2.2-.1.4.15.25.6.9 1.3 1.4.9.65 1.1.55 1.3.5.15-.05.4-.35.5-.5.1-.15.2-.15.35-.1l1.15.55c.15.05.25.1.3.2.05.15.05.55-.15.9-.2.4-.95.75-1.3.8-.35.05-.75.1-2.4-.9-1.55-.9-2.35-2.7-2.4-2.85-.05-.15-.5-.7-.5-1.35s.35-.95.5-1.1Z"
      fill="#25D366"
    />
  </svg>
);
const TelegramIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
    <circle cx="9" cy="9" r="7" fill="#229ED9" />
    <path d="M4.5 8.9 13 5.6l-1.4 7.3c-.1.5-.4.6-.8.4l-2.2-1.6-1.1 1c-.1.1-.2.2-.4.2l.15-2.1 3.9-3.5c.17-.15-.04-.24-.26-.09L6 9.8 4 9.15c-.5-.15-.5-.5.1-.75Z" fill="#fff" />
  </svg>
);
const TikTokIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
    <rect x="1.5" y="1.5" width="15" height="15" rx="4" fill="#000" />
    <path
      d="M10.4 3.8h1.4c.1.9.7 1.7 1.6 2v1.5c-.6 0-1.2-.2-1.7-.5v3.5a3 3 0 1 1-3-3c.1 0 .2 0 .3.02v1.5a1.5 1.5 0 1 0 1.4 1.5V3.8Z"
      fill="#fff"
    />
  </svg>
);
const OtherLinkIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
    <circle cx="9" cy="9" r="7" stroke={ICON_COLOR} strokeWidth="1.3" />
    <path d="M6.5 9.5a2 2 0 0 1 0-2.8l1-1a2 2 0 0 1 2.8 2.8" stroke={ICON_COLOR} strokeWidth="1.3" strokeLinecap="round" />
    <path d="M11.5 8.5a2 2 0 0 1 0 2.8l-1 1a2 2 0 0 1-2.8-2.8" stroke={ICON_COLOR} strokeWidth="1.3" strokeLinecap="round" />
  </svg>
);

const SOCIAL_PLATFORMS = [
  { value: 'instagram', label: 'Instagram', Icon: InstagramIcon },
  { value: 'facebook', label: 'Facebook', Icon: FacebookIcon },
  { value: 'twitter', label: 'Twitter', Icon: XIcon },
  { value: 'whatsapp', label: 'WhatsApp', Icon: WhatsAppIcon },
  { value: 'telegram', label: 'Telegram', Icon: TelegramIcon },
  { value: 'tiktok', label: 'TikTok', Icon: TikTokIcon },
  { value: 'other', label: 'Others', Icon: OtherLinkIcon },
];

const getSocialPlatform = value => SOCIAL_PLATFORMS.find(p => p.value === value) || SOCIAL_PLATFORMS[SOCIAL_PLATFORMS.length - 1];

/* ── Custom themed dropdown for picking a social platform — a native
   <select>'s open option list can't be styled (browser-owned UI), so
   this is a button + custom popover matching the Settings dropdown's
   visual language (rounded card, icon rows, red accents). ── */
const SocialPlatformSelect = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const selected = getSocialPlatform(value);

  useEffect(() => {
    const handleClickOutside = e => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div style={{ position: 'relative' }} ref={wrapRef}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        style={{
          width: '100%', boxSizing: 'border-box', display: 'flex', alignItems: 'center', gap: 10,
          border: `1.5px solid ${open ? RED : '#e5e7eb'}`, borderRadius: 8,
          padding: '10px 12px', fontSize: 13, color: '#111',
          background: '#fff', fontFamily: 'inherit', cursor: 'pointer',
          transition: 'border-color .15s',
        }}
      >
        <selected.Icon />
        <span style={{ flex: 1, textAlign: 'left' }}>{selected.label}</span>
        <span style={{ display: 'flex', transform: open ? 'rotate(90deg)' : 'none', transition: 'transform .15s' }}>
          <IconChevron />
        </span>
      </button>

      {open && (
        <div
          role="listbox"
          style={{
            position: 'absolute', top: 'calc(100% + 6px)', left: 0, right: 0,
            background: '#fff', borderRadius: 14, padding: 6,
            boxShadow: '0 12px 32px rgba(0,0,0,0.16), 0 2px 8px rgba(0,0,0,0.06)',
            zIndex: 20, display: 'flex', flexDirection: 'column', gap: 1,
          }}
        >
          {SOCIAL_PLATFORMS.map(p => {
            const isSelected = p.value === value;
            return (
              <button
                key={p.value}
                type="button"
                role="option"
                aria-selected={isSelected}
                onClick={() => { onChange(p.value); setOpen(false); }}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  width: '100%', border: 'none', borderRadius: 10,
                  padding: '10px 10px', fontSize: 13, fontFamily: 'inherit',
                  textAlign: 'left', cursor: 'pointer',
                  background: isSelected ? '#fff2f2' : 'transparent',
                  color: isSelected ? RED : '#1a1a1a',
                  fontWeight: isSelected ? 700 : 500,
                }}
                onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = '#f5f5f7'; }}
                onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = 'transparent'; }}
              >
                <p.Icon />
                <span>{p.label}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

const WebSocialLinkForm = ({ initialPlatform = 'instagram', initialTitle = '', initialUrl = '', onSave, onCancel }) => {
  const [platform, setPlatform] = useState(initialPlatform);
  const [customTitle, setCustomTitle] = useState(initialPlatform === 'other' ? initialTitle : '');
  const [url, setUrl] = useState(initialUrl);
  const [error, setError] = useState(null);

  const isOther = platform === 'other';

  const handleSave = () => {
    const title = isOther ? customTitle.trim() : getSocialPlatform(platform).label;
    if (!title || !url.trim()) {
      setError(isOther ? 'A name and a link are required.' : 'A link is required.');
      return;
    }
    onSave({ platform, title, url: normalizeUrl(url) });
  };

  return (
    <FieldCard>
      <FieldWrap>
        <FieldLabel>Social Media</FieldLabel>
        <SocialPlatformSelect value={platform} onChange={setPlatform} />
      </FieldWrap>

      {isOther && (
        <FieldWrap>
          <FieldLabel>Name</FieldLabel>
          <FieldInput value={customTitle} onChange={e => setCustomTitle(e.target.value)} placeholder="e.g. Yelp" />
        </FieldWrap>
      )}

      <FieldWrap style={{ marginBottom: 0 }}>
        <FieldLabel>Link</FieldLabel>
        <FieldInput value={url} onChange={e => setUrl(e.target.value)} placeholder="e.g. instagram.com/yourbusiness" icon={<IcoGlobe />} />
      </FieldWrap>

      {error && (
        <div style={{
          background: '#fff2f2', border: '1px solid #fca5a5',
          borderRadius: 8, padding: '10px 14px',
          fontSize: 13, color: RED, marginTop: 14,
        }}>
          {error}
        </div>
      )}

      <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
        <button type="button" onClick={onCancel} style={{
          flex: 1, padding: '10px 16px', borderRadius: 8,
          border: '1.5px solid #e5e7eb', background: '#fff',
          color: '#374151', fontSize: 13, fontWeight: 600,
          cursor: 'pointer', fontFamily: 'inherit',
        }}>
          Cancel
        </button>
        <button type="button" onClick={handleSave} style={{
          flex: 1, padding: '10px 16px', borderRadius: 8,
          border: 'none', background: RED, color: '#fff',
          fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
        }}>
          Save
        </button>
      </div>
    </FieldCard>
  );
};

const WebSocialModal = ({ onClose }) => {
  const [links, setLinks] = useState(loadWebSocialLinks);
  const [editingIndex, setEditingIndex] = useState(null); // number, 'new', or null
const deleteLink = index => {
  setLinks(prev => prev.filter((_, i) => i !== index));
  if (editingIndex === index) setEditingIndex(null);
};

  useEffect(() => {
    localStorage.setItem(WEB_SOCIAL_STORAGE_KEY, JSON.stringify(links));
  }, [links]);

  const handleBackdrop = e => { if (e.target === e.currentTarget) onClose(); };

  useEffect(() => {
    const handler = e => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  const saveLink = value => {
    if (editingIndex === 'new') {
      setLinks(prev => [...prev, value]);
    } else {
      setLinks(prev => prev.map((l, i) => (i === editingIndex ? value : l)));
    }
    setEditingIndex(null);
  };

  return (
    <div
      onClick={handleBackdrop}
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(17,24,39,0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1000, padding: 20,
        backdropFilter: 'blur(3px)',
      }}
    >
      <div style={{
        width: '100%', maxWidth: 480,
        borderRadius: 24,
        background: '#fff',
        boxShadow: '0 24px 64px rgba(0,0,0,0.22)',
        display: 'flex',
        flexDirection: 'column',
      }}>
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '20px 24px 16px',
        }}>
          <div style={{ fontSize: 18, fontWeight: 800, color: '#1a1a1a' }}>Web &amp; Social</div>
          <button
            onClick={onClose}
            style={{
              width: 32, height: 32, borderRadius: 8,
              border: '1.5px solid #e5e7eb', background: '#fff',
              cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
              <path d="M1 1l12 12M13 1L1 13" stroke="#555" strokeWidth="1.6" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: '0 24px 24px', display: 'flex', flexDirection: 'column', gap: 12 }}>
          {links.length === 0 && editingIndex === null && (
            <div style={{ fontSize: 13, color: '#9ca3af', padding: '8px 0 4px' }}>
              No links added yet.
            </div>
          )}

          {links.map((link, i) =>
            editingIndex === i ? (
              <WebSocialLinkForm
                key={i}
                initialPlatform={link.platform}
                initialTitle={link.title}
                initialUrl={link.url}
                onSave={saveLink}
                onCancel={() => setEditingIndex(null)}
              />
            ) : (
              <div
                key={i}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  border: '1.5px solid #e5e7eb', borderRadius: 8, padding: '12px 14px', gap: 10,
                  background: '#fff',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                  <span style={{ flexShrink: 0, display: 'flex' }}>
                    {React.createElement(getSocialPlatform(link.platform).Icon)}
                  </span>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: ICON_COLOR }}>{link.title}</div>
                    <div style={{
                      fontSize: 12, color: '#9ca3af', overflow: 'hidden',
                      textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>
                      {link.url}
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                  <button
                    onClick={() => setEditingIndex(i)}
                    aria-label={`Edit ${link.title}`}
                    style={{
                      width: 32, height: 32, borderRadius: 8,
                      border: '1.5px solid #e5e7eb', background: '#fff',
                      cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}
                  >
                    <IconEdit />
                  </button>
                  <button
                    onClick={() => deleteLink(i)}
                    aria-label={`Delete ${link.title}`}
                    style={{
                      width: 32, height: 32, borderRadius: 8,
                      border: '1.5px solid #fca5a5', background: '#fff',
                      cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}
                  >
                    <IconTrash />
                  </button>
                </div>
              </div>
            ),
          )}

          {editingIndex === 'new' && (
            <WebSocialLinkForm onSave={saveLink} onCancel={() => setEditingIndex(null)} />
          )}

          {editingIndex === null && links.length < MAX_WEB_SOCIAL_LINKS && (
            <button
              onClick={() => setEditingIndex('new')}
              style={{
                marginTop: 4, padding: '10px 0', borderRadius: 8,
                border: `1.5px dashed ${RED}`, background: 'transparent',
                color: RED, fontWeight: 700, fontSize: 13, cursor: 'pointer',
              }}
            >
              + Add Link
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

/* ── Menu item ──────────────────────────────────────────────────── */
const MenuItem = ({ icon, label, onClick }) => (
  <button className="acc-menu-item" onClick={onClick}>
    <div className="acc-menu-item-left">
      <div className="acc-menu-item-icon">{icon}</div>
      <span className="acc-menu-item-label">{label}</span>
    </div>
    <IconChevron />
  </button>
);

/* ── Section card ───────────────────────────────────────────────── */
const SectionCard = ({ icon, title, children }) => (
  <div className="acc-section">
    <div className="acc-section-header">
      {icon}
      <span className="acc-section-title">{title}</span>
    </div>
    {children}
  </div>
);

/* ── Info row ───────────────────────────────────────────────────── */
const InfoRow = ({ label, value, isLast }) => (
  <div className={`acc-info-row${isLast ? ' acc-info-row--last' : ''}`}>
    <span className="acc-info-label">{label}</span>
    <span className="acc-info-value">{value}</span>
  </div>
);

/* ── Main component ─────────────────────────────────────────────── */
const MyBusinessAccount = ({ intl, dispatch, fetching, data, membershipData, membershipFetching, points, referralData }) => {
  useEffect(() => {
    dispatch(actions.getInfo());
    dispatch(membershipInfoActions.getMembershipInfo());
    dispatch(pointsBalancesActions.getPointsBalances());
  }, [dispatch]);

  const navigate = useNavigate();
  const [businessEditModalOpen, setBusinessEditModalOpen] = useState(false);
  const [membershipModal, setMembershipModal] = useState(false);
  const [webSocialModal, setWebSocialModal] = useState(false);
  const [contactModal, setContactModal] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  // console.log("[MyBusinessAccount] render — data:", data, "membershipData:", membershipData);

  // BusinessInfoEditModal (shared with MyCoupons.js) hands back its own
  // field shape — map it to what actions.updateInfo expects. socialLinks
  // isn't part of updateInfo's payload today, so it's intentionally
  // dropped here.
  //
  // NOTE: this previously did `await dispatch(actions.updateInfo(values))`
  // directly. A plain dispatch() call returns the action object itself,
  // not a promise tied to the actual request — MyCoupons.js instead routes
  // through dispatchAsAsync(action, dispatch) (already imported above, but
  // never used) for exactly this reason. Switched to match that pattern.
  const handleBusinessInfoSave = async modalValues => {
    setSubmitError(null);

    // Start from the full existing record — the saga diffs whatever we
    // send here against `state.businessInfo.data`, and treats any field
    // we simply omit as "changed to undefined" (that's what caused the
    // categories/subcategories crash earlier, and now this boolean
    // validation error for fields like `verified` / `has_joined_points_
    // program` that this modal never touches). Spreading `data` first
    // means untouched fields match the original exactly and drop out of
    // the diff, so only what the modal actually changed gets sent.
    const payload = {
      ...data,
      name: modalValues.businessName,
      contact_name: modalValues.contactName,
      phone: modalValues.phone,
      email: modalValues.email,
      website: modalValues.website,
      street: modalValues.streetAddress,
      city: modalValues.city,
      province: modalValues.province,
      postal_code: modalValues.postalCode,
      country: modalValues.country,
      terms_and_conditions: modalValues.termsAndConditions,
      logo: modalValues.logo,
      cover_image: modalValues.banner,
    };



    try {
      const result = await dispatchAsAsync(actions.updateInfo(payload), dispatch);
      
      // updateInfo's saga resolves with nothing on success — it only ever
      // passes a payload to resolve() on the error paths (see saga.js:
      // `safeResolveAsync(resolve)` vs `safeResolveAsync(resolve, errors)`).
      // So falsy = success here, not `result?.success` (that shape belongs
      // to a different action in MyCoupons.js, not this one).
      if (!result) {
        setBusinessEditModalOpen(false);
      } else if (result[FORM_ERROR]) {
        setSubmitError(result[FORM_ERROR]);
      } else {
        // formatApiErrors(response.data) shape: { field: message, ... }
        const message = Object.entries(result)
          .map(([field, msg]) => `${field}: ${Array.isArray(msg) ? msg.join(', ') : msg}`)
          .join(' ');
        setSubmitError(message || 'Failed to save. Please try again.');
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[MyBusinessAccount] handleBusinessInfoSave — threw:', e);
      setSubmitError('Failed to save. Please try again.');
    }
  };

  return (
    <div className="acc-page">
      <AuthProtected />
      <NavBar />

      <div className="acc-container">

        {/* ── Heading ── */}
        <h1 className="acc-heading">My <strong>Account</strong></h1>

        {/* ── Verification warning ── */}
        {data?.verified === false && (
          <div className="acc-warning">
            <img src={cautionImg} alt="" />
            <div>
              <strong>Your Account is Under Review</strong>
              <p>We're reviewing your account, which typically takes up to 2 business days. In the meantime, feel free to explore your business page and save coupons as drafts.</p>
            </div>
          </div>
        )}

        {/* ── Top grid wrap ── */}
        <div className="acc-top-grid-wrap">
          <div className="acc-top-grid">

            {/* LEFT: BusinessCard */}
            <div className="bc-account">
              <BusinessCard businessInfo={data} data={data} />
            </div>

            {/* CENTER: Business Information */}
            <div className="acc-card acc-card-info">
              <div className="acc-card-stripe" />

              <div className="acc-card-header">
                <div className="acc-card-title">
                  <div className="acc-card-icon">
                    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                      <rect x="2" y="4" width="14" height="10" rx="2" stroke={ICON_COLOR} strokeWidth="1.4"/>
                      <path d="M6 4V3a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v1" stroke={ICON_COLOR} strokeWidth="1.4"/>
                    </svg>
                  </div>
                  <span>Business Information</span>
                </div>
                <button className="acc-edit-btn" onClick={() => setBusinessEditModalOpen(true)}>
                  <IconEdit />
                </button>
              </div>

              <div className="acc-card-body">
                <InfoRow label="Business Name" value={safeStr(data?.business_name || data?.name)} />
                <InfoRow label="Contact Name"  value={safeStr(data?.contact_name  || data?.first_name)} />
                <InfoRow label="Phone"         value={safeStr(data?.phone)} />
                <InfoRow label="Location"      value={safeStr(data?.city || data?.location)} isLast />
              </div>
            </div>

            {/* RIGHT: Membership */}
            <div className="acc-card acc-card-membership">
              <div className="acc-card-header">
                <div className="acc-card-title">
                  <div className="acc-card-icon">
                    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                      <circle cx="5"  cy="6"  r="2.5" stroke={ICON_COLOR} strokeWidth="1.3"/>
                      <circle cx="13" cy="6"  r="2.5" stroke={ICON_COLOR} strokeWidth="1.3"/>
                      <circle cx="9"  cy="12" r="2.5" stroke={ICON_COLOR} strokeWidth="1.3"/>
                      <path d="M5 8.5c0 1.5 1.3 2.7 3 3M13 8.5c0 1.5-1.3 2.7-3 3" stroke={ICON_COLOR} strokeWidth="1.2" strokeLinecap="round"/>
                    </svg>
                  </div>
                  <span>Membership</span>
                </div>
                <button className="acc-edit-btn" onClick={() => setMembershipModal(true)}><IconEdit /></button>
              </div>
              <div className="acc-premium-wrap">
                {membershipData?.is_free_plan ? (
                  <BusinessCard businessInfo={data} data={data} />
                ) : (
                  <PremiumCard
                    businessName={data?.business_name || data?.name}
                    points={points}
                    activeCoupons={
                      membershipData?.available_coupons != null && membershipData?.coupon_capacity != null
                        ? `${membershipData.available_coupons}/${membershipData.coupon_capacity}`
                        : undefined
                    }
                    code={referralData?.referral_code}
                    renewalDate={formatRenewalDate(membershipData?.renewal_date)}
                    autoRenewal={membershipData?.auto_renewal == null ? undefined : (membershipData.auto_renewal ? 'Yes' : 'No')}
                  />
                )}
              </div>
            </div>

          </div>
        </div>

        {/* ── Membership modal ── */}
        {membershipModal && (
          <MembershipModal onClose={() => setMembershipModal(false)} navigate={navigate} />
        )}

        {/* ── Web & Social modal ── */}
        {SHOW_WEB_SOCIAL && webSocialModal && (
          <WebSocialModal onClose={() => setWebSocialModal(false)} />
        )}

        {/* ── Contact Us modal ── */}
        {contactModal && (
          <ContactUsModal onClose={() => setContactModal(false)} />
        )}

        {/* ── Business Info edit modal (shared with MyCoupons.js) ── */}
        <BusinessInfoEditModal
          isOpen={businessEditModalOpen}
          businessInfo={data}
          onClose={() => setBusinessEditModalOpen(false)}
          onSave={handleBusinessInfoSave}
          saving={fetching}
          error={submitError}
        />

        {/* ── Bottom settings grid ── */}
        <div className="acc-settings-grid">

          <SectionCard icon={<IconShield />} title="Account & Security">
            {/* <MenuItem icon={<svg width="18" height="18" viewBox="0 0 18 18" fill="none"><rect x="2" y="4" width="14" height="10" rx="2" stroke={ICON_COLOR} strokeWidth="1.3"/><path d="M6 4V3a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v1" stroke={ICON_COLOR} strokeWidth="1.3"/></svg>} label="Business Details" /> */}
            {SHOW_WEB_SOCIAL && (
              <MenuItem icon={<svg width="18" height="18" viewBox="0 0 18 18" fill="none"><rect x="2" y="5" width="14" height="8" rx="2" stroke={ICON_COLOR} strokeWidth="1.3"/><path d="M6 5V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v1" stroke={ICON_COLOR} strokeWidth="1.3"/><circle cx="9" cy="9" r="1.5" fill={ICON_COLOR}/></svg>} label="Web & Social" onClick={() => setWebSocialModal(true)} />
            )}
            <MenuItem icon={<svg width="18" height="18" viewBox="0 0 18 18" fill="none"><circle cx="9" cy="7" r="3" stroke={ICON_COLOR} strokeWidth="1.3"/><path d="M3 16c0-3.3 2.7-6 6-6s6 2.7 6 6" stroke={ICON_COLOR} strokeWidth="1.3" strokeLinecap="round"/></svg>} label="Email & Password" onClick={() => navigate('/account/settings')} />
            <MenuItem
  icon={<svg width="18" height="18" viewBox="0 0 18 18" fill="none"><rect x="2" y="4" width="14" height="10" rx="2" stroke={ICON_COLOR} strokeWidth="1.3"/><path d="M6 4V3a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v1" stroke={ICON_COLOR} strokeWidth="1.3"/></svg>}
  label="Business Information"
  onClick={() => setBusinessEditModalOpen(true)}
/>
          </SectionCard>

          <SectionCard icon={<IconSettings2 />} title="Preferences & Support">
            {/* <MenuItem icon={<svg width="18" height="18" viewBox="0 0 18 18" fill="none"><path d="M9 2a7 7 0 1 0 0 14A7 7 0 0 0 9 2Z" stroke={ICON_COLOR} strokeWidth="1.3"/><path d="M9 8v4M9 6h.01" stroke={ICON_COLOR} strokeWidth="1.4" strokeLinecap="round"/></svg>} label="Notifications & Preferences" /> */}
            <MenuItem icon={<svg width="18" height="18" viewBox="0 0 18 18" fill="none"><path d="M3 5a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v6a2 2 0 0 1-2 2H7l-4 3V5Z" stroke={ICON_COLOR} strokeWidth="1.3" strokeLinejoin="round"/></svg>} label="Blogs"  onClick={() => navigate('/business-blogs')} />
            <MenuItem icon={<svg width="18" height="18" viewBox="0 0 18 18" fill="none"><circle cx="9" cy="9" r="7" stroke={ICON_COLOR} strokeWidth="1.3"/><path d="M6.5 7c0-1.4 1.1-2.5 2.5-2.5S11.5 5.6 11.5 7c0 2-2.5 2.5-2.5 4" stroke={ICON_COLOR} strokeWidth="1.3" strokeLinecap="round"/><circle cx="9" cy="13" r=".75" fill={ICON_COLOR}/></svg>} label="Contact Us" onClick={() => setContactModal(true)} />
          </SectionCard>

          <SectionCard icon={<IconDollar />} title="Financials & Membership">
            <MenuItem icon={<svg width="18" height="18" viewBox="0 0 18 18" fill="none"><rect x="2" y="3" width="14" height="12" rx="2" stroke={ICON_COLOR} strokeWidth="1.3"/><path d="M2 7h14M6 11h2M10 11h2" stroke={ICON_COLOR} strokeWidth="1.3" strokeLinecap="round"/></svg>} label="Membership Management" onClick={() => setMembershipModal(true)} />
            {SHOW_BILLING && (
              <MenuItem icon={<svg width="18" height="18" viewBox="0 0 18 18" fill="none"><rect x="2" y="5" width="14" height="9" rx="1.5" stroke={ICON_COLOR} strokeWidth="1.3"/><path d="M6 5V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v1" stroke={ICON_COLOR} strokeWidth="1.3"/><circle cx="6.5" cy="11" r="1" fill={ICON_COLOR}/></svg>} label="Billing & Transactions" />
            )}
          </SectionCard>

        </div>
      </div>
    </div>
  );
};

MyBusinessAccount.propTypes = {
  intl:               PropTypes.object.isRequired,
  fetching:           PropTypes.bool,
  data:               PropTypes.object,
  dispatch:           PropTypes.func,
  membershipData:     PropTypes.object,
  membershipFetching: PropTypes.bool,
  referralData:       PropTypes.object,
};

const mapStateToProps = state => ({
  fetching:           state.businessInfo.fetching,
  data:               state.businessInfo.data,
  membershipData:     state.memberships?.info?.data?.current_plan || {},
  membershipFetching: !!(state.memberships?.info?.fetching),
  points:             state.pointsBalance?.data?.points,
  referralData:       state.businessReferralProgram?.data || {},
});

export default connect(mapStateToProps)(injectIntl(MyBusinessAccount));
